import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, X, CalendarDays } from 'lucide-react';
import { Card, SectionHeader, Btn, Modal, Field, Input, EmptyState } from '../components/UI';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW    = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

// Pre-seed Pakistan 2025 public holidays (admin can add/remove freely)
const DEFAULT_PK_HOLIDAYS_2025 = [
  { id:'pk1',  date:'2025-02-05', name:'Kashmir Day' },
  { id:'pk2',  date:'2025-03-23', name:'Pakistan Day' },
  { id:'pk3',  date:'2025-04-14', name:'Eid ul-Fitr' },
  { id:'pk4',  date:'2025-04-15', name:'Eid ul-Fitr (2nd day)' },
  { id:'pk5',  date:'2025-05-01', name:'Labour Day' },
  { id:'pk6',  date:'2025-06-06', name:'Eid ul-Adha' },
  { id:'pk7',  date:'2025-06-07', name:'Eid ul-Adha (2nd day)' },
  { id:'pk8',  date:'2025-06-27', name:'Muharram / Ashura' },
  { id:'pk9',  date:'2025-08-14', name:'Independence Day' },
  { id:'pk10', date:'2025-09-05', name:'Eid Milad-un-Nabi' },
  { id:'pk11', date:'2025-11-09', name:"Iqbal Day" },
  { id:'pk12', date:'2025-12-25', name:'Quaid-e-Azam Day / Christmas' },
];

export default function HolidayCalendar({ store, persist, showToast, readOnly = false }) {
  // Seed defaults if no holidays stored yet
  const holidays = store.holidays || DEFAULT_PK_HOLIDAYS_2025;

  const now = new Date();
  const [year,      setYear]      = useState(now.getFullYear());
  const [month,     setMonth]     = useState(now.getMonth());
  const [showModal, setShowModal] = useState(false);
  const [form,      setForm]      = useState({ date: '', name: '' });

  const holidayDates = new Set(holidays.map(h => h.date));

  function prevMonth() { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); }
  function nextMonth() { if (month === 11) { setMonth(0);  setYear(y => y + 1); } else setMonth(m => m + 1); }

  function addHoliday() {
    if (!form.date || !form.name.trim()) { showToast('Date and name required.', 'error'); return; }
    if (holidayDates.has(form.date)) { showToast('A holiday already exists on that date.', 'error'); return; }
    const entry = { id: Date.now().toString(), date: form.date, name: form.name.trim() };
    persist({ ...store, holidays: [...holidays, entry].sort((a, b) => a.date.localeCompare(b.date)) });
    setForm({ date: '', name: '' });
    setShowModal(false);
    showToast('Holiday added.');
  }

  function removeHoliday(id) {
    persist({ ...store, holidays: holidays.filter(h => h.id !== id) }, { deletedIds: { holidays: [id] } });
    showToast('Holiday removed.');
  }

  // Build calendar grid
  const daysInMonth  = new Date(year, month + 1, 0).getDate();
  const firstDOW     = new Date(year, month, 1).getDay(); // 0=Sun
  const offset       = firstDOW === 0 ? 6 : firstDOW - 1; // Mon-first offset

  // Holidays in this month
  const ymStr        = `${year}-${String(month + 1).padStart(2, '0')}`;
  const thisMonthH   = holidays.filter(h => h.date.startsWith(ymStr));

  // All upcoming/past holidays list
  const upcomingH    = holidays.filter(h => h.date >= ymStr).slice(0, 8);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {!readOnly && (
        <div>
          <h1 className="t-h1">Public Holidays</h1>
          <p className="t-sub" style={{ marginTop:4 }}>Scheduled company closures · employees can't clock in on these days.</p>
        </div>
      )}
      {/* Calendar card */}
      <Card>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Btn size="sm" variant="ghost" onClick={prevMonth}><ChevronLeft size={14} /></Btn>
            <span style={{ fontFamily:'var(--serif)', fontWeight: 400, fontSize: 15, color: 'var(--text)', minWidth: 150, textAlign: 'center' }}>{MONTHS[month]} {year}</span>
            <Btn size="sm" variant="ghost" onClick={nextMonth}><ChevronRight size={14} /></Btn>
          </div>
          {!readOnly && (
            <Btn size="sm" onClick={() => { setForm({ date: ymStr + '-01', name: '' }); setShowModal(true); }}>
              <Plus size={12} />Mark holiday
            </Btn>
          )}
        </div>

        <div style={{ padding: '1rem 1.25rem' }}>
          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 4, marginBottom: 6 }}>
            {DOW.map(d => (
              <div key={d} style={{ textAlign: 'center', fontFamily:'var(--mono)', fontSize: 10, fontWeight: 400, textTransform:'uppercase', letterSpacing:'0.1em', color: 'var(--text3)', padding: '3px 0' }}>{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 4 }}>
            {/* Blank offset cells */}
            {Array(offset).fill(null).map((_, i) => <div key={'b' + i} />)}

            {/* Day cells */}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day      = i + 1;
              const dateStr  = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dowNum   = new Date(year, month, day).getDay(); // 0=Sun
              const isWknd   = dowNum === 0 || dowNum === 6;
              const isHol    = holidayDates.has(dateStr);
              const isToday  = dateStr === new Date().toISOString().slice(0, 10);

              let bg     = isHol  ? 'var(--red-bg)'     : isWknd ? 'transparent'   : 'var(--bg3)';
              let border = isHol  ? 'var(--red)'        : isToday ? 'var(--accent)' : isWknd ? 'transparent' : 'var(--border)';
              let color  = isHol  ? 'var(--red)'        : isWknd ? 'var(--text3)'  : 'var(--text)';

              return (
                <div
                  key={dateStr}
                  title={isHol ? holidays.find(h => h.date === dateStr)?.name : undefined}
                  style={{
                    borderRadius:'var(--radius)', padding: '7px 3px', textAlign: 'center',
                    fontFamily:'var(--mono)', fontVariantNumeric:'lining-nums tabular-nums',
                    fontSize: 11, fontWeight: isHol || isToday ? 700 : 500,
                    background: bg, border: `1px solid ${border}`, color,
                    opacity: isWknd && !isHol ? 0.35 : 1,
                    cursor: isHol && !readOnly ? 'pointer' : 'default',
                    transition: 'all 0.1s',
                  }}
                  onClick={() => {
                    if (!readOnly && isHol) {
                      const h = holidays.find(hh => hh.date === dateStr);
                      if (h && window.confirm(`Remove "${h.name}"?`)) removeHoliday(h.id);
                    }
                  }}
                >
                  <div>{day}</div>
                  {isHol && <div style={{ fontFamily:'var(--mono)', fontWeight:400, letterSpacing:'0.1em', fontSize: 7, marginTop: 1, opacity: 0.85 }}>HOL</div>}
                </div>
              );
            })}
          </div>

          {/* This month's holidays legend */}
          {thisMonthH.length > 0 && (
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ fontFamily:'var(--mono)', fontSize: 10, fontWeight: 400, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>Holidays this month</div>
              {thisMonthH.map(h => (
                <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--red)', flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: 'var(--text2)', fontFamily: 'var(--mono)', fontVariantNumeric:'lining-nums tabular-nums', marginRight: 4 }}>{h.date.slice(5)}</span>
                  <span style={{ fontSize: 12, color: 'var(--text)' }}>{h.name}</span>
                  {!readOnly && (
                    <button onClick={() => removeHoliday(h.id)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', display: 'flex', padding: 2 }}>
                      <X size={11} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* All holidays — month-grouped with date badges + yearly overview */}
      {!readOnly && (
        <div className="grid-chart">
          <Card>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px', borderBottom:'1px solid var(--border)' }}>
              <span className="t-h3">All holidays <span style={{ fontFamily:'var(--mono)', fontVariantNumeric:'lining-nums tabular-nums', fontSize:12, fontWeight:600, color:'var(--text3)', background:'var(--bg3)', padding:'3px 9px', borderRadius:'var(--radius)', marginLeft:4 }}>{holidays.length}</span></span>
              <Btn size="sm" variant="primary" onClick={() => { setForm({ date: '', name: '' }); setShowModal(true); }}><Plus size={13} />Add New Holiday</Btn>
            </div>
            {holidays.length === 0
              ? <EmptyState message="No holidays marked yet." />
              : (() => {
                  const sorted = holidays.slice().sort((a,b) => a.date.localeCompare(b.date));
                  const groups = {};
                  sorted.forEach(h => { const k = h.date.slice(0,7); (groups[k] = groups[k] || []).push(h); });
                  return Object.keys(groups).sort().map(k => {
                    const [yy,mm] = k.split('-');
                    return (
                      <div key={k}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 22px', background:'var(--bg3)' }}>
                          <span style={{ fontFamily:'var(--serif)', fontWeight:400, fontSize:14, color:'var(--text)' }}>{MONTHS[Number(mm)-1]} {yy}</span>
                          <span className="t-label">{groups[k].length} {groups[k].length>1?'holidays':'holiday'}</span>
                        </div>
                        {groups[k].map(h => {
                          const dd = new Date(h.date+'T12:00:00');
                          return (
                            <div key={h.id} style={{ display:'flex', alignItems:'center', gap:16, padding:'14px 22px', borderBottom:'1px solid var(--border)' }}>
                              <div style={{ width:54, flexShrink:0, textAlign:'center', background:'rgba(169,137,83,0.12)', borderRadius:'var(--radius)', padding:'8px 0' }}>
                                <div style={{ fontFamily:'var(--mono)', fontSize:10, fontWeight:400, letterSpacing:'0.1em', color:'var(--accent)', textTransform:'uppercase' }}>{MONTHS[dd.getMonth()].slice(0,3)}</div>
                                <div style={{ fontFamily:'var(--mono)', fontVariantNumeric:'lining-nums tabular-nums', fontSize:20, fontWeight:700, color:'var(--text)', lineHeight:1.1 }}>{dd.getDate()}</div>
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontFamily:'var(--serif)', fontSize:15, fontWeight:400, color:'var(--text)' }}>{h.name}</div>
                                <div style={{ fontSize:12.5, color:'var(--text3)', marginTop:1 }}>{dd.toLocaleDateString('en-GB',{weekday:'long'})} · Public</div>
                              </div>
                              <Btn size="sm" variant="danger" onClick={() => removeHoliday(h.id)}><X size={13} /></Btn>
                            </div>
                          );
                        })}
                      </div>
                    );
                  });
                })()
            }
          </Card>

          {/* Yearly overview navy card */}
          <div>
            {(() => {
              const todayS = new Date().toISOString().slice(0,10);
              const remaining = holidays.filter(h => h.date >= todayS).length;
              return (
                <Card className="aura" style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'26px 28px', color:'var(--text)' }}>
                  <div style={{ fontFamily:'var(--mono)', fontSize:11, fontWeight:400, letterSpacing:'0.16em', textTransform:'uppercase', color:'var(--text3)', marginBottom:12 }}>Yearly Overview</div>
                  <div style={{ fontFamily:'var(--serif)', fontVariantNumeric:'lining-nums tabular-nums', fontSize:48, fontWeight:400, letterSpacing:'-0.01em', lineHeight:1, color:'var(--accent)' }}>{remaining}</div>
                  <div style={{ fontSize:15, color:'var(--text2)', marginTop:8 }}>Public holidays remaining</div>
                </Card>
              );
            })()}
          </div>
        </div>
      )}

      {/* Read-only upcoming list for employee portal */}
      {readOnly && upcomingH.length > 0 && (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius:'var(--radius)', overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontFamily:'var(--mono)', fontSize: 12, fontWeight: 400, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Upcoming holidays</div>
          {upcomingH.map((h, i) => (
            <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: i < upcomingH.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--red)', flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', fontVariantNumeric:'lining-nums tabular-nums', minWidth: 80 }}>{h.date}</span>
              <span style={{ fontSize: 13, color: 'var(--text)' }}>{h.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Add holiday modal (admin only) */}
      {!readOnly && (
        <Modal open={showModal} onClose={() => setShowModal(false)} title="Mark public holiday">
          <Field label="Date">
            <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} autoFocus />
          </Field>
          <Field label="Holiday name">
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Pakistan Day" />
          </Field>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: '1rem', background: 'var(--bg3)', padding: '9px 12px', borderRadius: 'var(--radius)' }}>
            ℹ️ Employees will not be able to clock in or out on this day. It will appear as a red holiday on their calendar.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn onClick={() => setShowModal(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={addHoliday}>Mark holiday</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
